/**
 * Journeys — multi-step sequences that run over days without anyone
 * remembering to send them.
 *
 * Nothing goes out on its own: a due step lands in the queue, and a person
 * opens each message in their mail client or WhatsApp. That is deliberate —
 * the workspace has no delivery pipeline, and pretending otherwise would be
 * worse than making the hand-off explicit.
 */
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { Card, CardHead, CardBody, EmptyState, Avatar, Switch, Pill } from '../../components/Primitives.jsx';
import { TileRow } from '../../components/StatCards.jsx';
import { STEP_TYPES, channelLabel, channelPill } from '../../constants/marketing.js';
import { journeyStats, stepLabel } from '../../domain/marketing.js';

const stepIcon = type => (STEP_TYPES.find(t => t[0] === type) || ['', '', 'send'])[2];

export default function Journeys() {
  const { mkt, setSheet, toggleJourney, removeMarketingRecord, runJourneys, clearQueue, toast } = useApp();

  const journeys = mkt.journeys || [];
  const pending = (mkt.queue || []).filter(q => q.status === 'pending');

  const totals = journeys.reduce((acc, j) => {
    const st = journeyStats(j, mkt);
    acc.active += st.active;
    acc.done += st.done;
    acc.exited += st.exited;
    return acc;
  }, { active: 0, done: 0, exited: 0 });

  const remove = journey => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the "${journey.name}" journey? Enrolments go with it.`)) return;
    removeMarketingRecord('journeys', journey.id);
  };

  const runNow = () => {
    const plan = runJourneys({ announce: true });
    if (!plan.queued.length && !plan.moved) toast('Nothing to do — no one is due a step yet');
  };

  const discardAll = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Discard everything queued?')) clearQueue();
  };

  return (
    <>
      <TileRow
        tiles={[
          ['Journeys', journeys.length, 'flow', 'g-pink'],
          ['Running', journeys.filter(j => j.enabled).length, 'zap', 'g-green'],
          ['People enrolled', totals.active, 'users', 'g-blue'],
          ['Queued to send', pending.length, 'send', 'g-amber'],
          ['Completed', totals.done, 'check', 'g-cyan'],
          ['Exited early', totals.exited, 'split', 'g-purple']
        ]}
      />

      {pending.length ? (
        <Card>
          <CardHead icon="send" tone="g-amber" title="Waiting to go out" count={pending.length}>
            <button type="button" className="btn sm" onClick={discardAll}>Discard all</button>
            <button type="button" className="btn sm primary" onClick={() => setSheet({ kind: 'queue' })}>
              Open the queue
            </button>
          </CardHead>
          <div className="qlist">
            {pending.slice(0, 6).map(x => (
              <div className="q" key={x.id} onClick={() => setSheet({ kind: 'queue' })}>
                <Avatar name={x.name} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b>{x.name}</b>
                  <div className="why">{x.jname} · step {x.step + 1} · {channelLabel(x.channel)}</div>
                </div>
                <Pill tone={channelPill(x.channel)}>{channelLabel(x.channel)}</Pill>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Card>
        <CardHead icon="flow" tone="g-pink" title="Journeys" count={journeys.length}>
          <button type="button" className="btn sm" onClick={runNow}><Icon name="refresh" />Run them now</button>
          <button type="button" className="btn sm primary" onClick={() => setSheet({ kind: 'journey' })}>
            <Icon name="plus" />New journey
          </button>
        </CardHead>

        <CardBody>
          {journeys.length ? journeys.map(j => {
            const st = journeyStats(j, mkt);
            const audience = mkt.audiences.find(a => a.id === j.audience);
            const steps = j.steps || [];

            return (
              <div className="jrow" key={j.id}>
                <div className="jhead">
                  <Switch on={j.enabled} onClick={() => toggleJourney(j.id)} title="Run or pause" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b>{j.name}</b>
                    <div className="dim" style={{ fontSize: 12 }}>
                      {audience ? audience.name : 'no audience'} · {steps.length} steps ·{' '}
                      {st.active} active, {st.done} completed
                      {st.pending ? <> · <span style={{ color: 'var(--amber)' }}>{st.pending} queued</span></> : null}
                    </div>
                  </div>
                  <button type="button" className="btn sm" onClick={() => setSheet({ kind: 'journey', id: j.id })}>
                    <Icon name="edit" />
                  </button>
                  <button type="button" className="btn sm danger" onClick={() => remove(j)}>
                    <Icon name="trash" />
                  </button>
                </div>

                <div className="jflow">
                  {steps.length ? steps.map((s, i) => (
                    <span key={i} style={{ display: 'contents' }}>
                      <div className={`jstep ${s.type}`}>
                        <Icon name={stepIcon(s.type)} />
                        <span>{stepLabel(s)}</span>
                      </div>
                      {i < steps.length - 1 ? <span className="jarr">→</span> : null}
                    </span>
                  )) : (
                    <span className="muted">No steps yet — open it and add the first one.</span>
                  )}
                </div>
              </div>
            );
          }) : (
            <EmptyState icon="flow" tone="g-pink" title="No journeys yet">
              A journey sends a sequence over days without you remembering to.
            </EmptyState>
          )}
        </CardBody>
      </Card>
    </>
  );
}
